'use strict';

import { Helper } from '../helpers/Helper.js';

/**
 * Replaces the WP media library on my-account campaign message editors with a private file upload.
 */
export class CampaignMessageMediaUpload {
	constructor() {
		this.#mountAll();

		if ( typeof window.tinymce !== 'undefined' ) {
			window.tinymce.on( 'AddEditor', () => {
				this.#mountAll();
			} );
		}
	}

	#mountAll() {
		document
			.querySelectorAll( '.levcharity-campaign-message-private-upload-flag' )
			.forEach( ( flag ) => {
				const editorId = String( flag.value || '' ).trim();
				if ( editorId ) {
					this.#mount( editorId );
				}
			} );
	}

	#mount( editorId ) {
		const wrap = document.getElementById( `wp-${ editorId }-wrap` );
		if ( wrap ) {
			this.#mountWpEditor( editorId, wrap );
			return;
		}

		this.#mountPlainTextarea( editorId );
	}

	#createMediaControls() {
		const button = document.createElement( 'button' );
		button.type = 'button';
		button.className =
			'button insert-media add_media levcharity-private-editor-media-button';
		button.innerHTML = `<span class="wp-media-buttons-icon"></span> ${ this.#t( 'Add Media' ) }`;

		const input = document.createElement( 'input' );
		input.type = 'file';
		input.accept = 'image/*';
		input.hidden = true;

		return { button, input };
	}

	#mountWpEditor( editorId, wrap ) {
		const tools = wrap.querySelector( '.wp-editor-tools' );
		if ( ! tools || tools.querySelector( '.levcharity-private-editor-media-button' ) ) {
			return;
		}

		let mediaButtons = tools.querySelector( '.wp-media-buttons' );
		if ( ! mediaButtons ) {
			mediaButtons = document.createElement( 'div' );
			mediaButtons.className =
				'wp-media-buttons levcharity-private-media-buttons';
			tools.prepend( mediaButtons );
		}

		const { button, input } = this.#createMediaControls();
		mediaButtons.prepend( button, input );
		this.#bind( editorId, button, input );
	}

	#mountPlainTextarea( editorId ) {
		const textarea = document.getElementById( editorId );
		const field = textarea?.closest( '.form-field' );
		if ( ! field || field.querySelector( '.levcharity-private-editor-media-button' ) ) {
			return;
		}

		const toolbar = document.createElement( 'div' );
		toolbar.className =
			'levcharity-private-textarea-media-toolbar levcharity-private-media-buttons';

		const { button, input } = this.#createMediaControls();
		toolbar.append( button, input );
		field.insertBefore( toolbar, textarea );
		this.#bind( editorId, button, input );
	}

	#bind( editorId, button, input ) {
		button.addEventListener( 'click', ( event ) => {
			event.preventDefault();
			input.click();
		} );

		input.addEventListener( 'change', async () => {
			const file = input.files?.[ 0 ];
			input.value = '';
			if ( ! file || ! file.type.startsWith( 'image/' ) ) {
				return;
			}

			const ajaxUrl =
				typeof window.admin_ajax !== 'undefined' &&
				window.admin_ajax?.[ 0 ]
					? window.admin_ajax[ 0 ]
					: '';
			if ( ! ajaxUrl ) {
				return;
			}

			const previousDisabled = button.disabled;
			button.disabled = true;

			try {
				const formData = new FormData();
				formData.append( 'action', 'levcaharity_upload_images' );
				formData.append( 'new_images[]', file );

				const response = await Helper.request(
					ajaxUrl,
					formData,
					{ headers: {} },
					false,
					false
				);

				const imageUrl = String(
					response?.data?.image_urls?.[ 0 ] || ''
				).trim();
				if ( ! imageUrl ) {
					return;
				}

				this.#insertImage( editorId, imageUrl, file.name );
			} catch {
				// Upload errors are surfaced by Helper.request rejection.
			} finally {
				button.disabled = previousDisabled;
			}
		} );
	}

	#insertImage( editorId, imageUrl, altText ) {
		const safeUrl = imageUrl.replace( /"/g, '&quot;' );
		const safeAlt = String( altText || '' )
			.replace( /"/g, '&quot;' )
			.trim();
		const html = `<img src="${ safeUrl }" alt="${ safeAlt }" />`;

		if ( typeof window.tinymce !== 'undefined' ) {
			const editor = window.tinymce.get( editorId );
			if ( editor && ! editor.isHidden() ) {
				editor.insertContent( html );
				editor.fire( 'change' );
				return;
			}
		}

		const textarea = document.getElementById( editorId );
		if ( textarea ) {
			textarea.value = `${ textarea.value }${ html }`;
			textarea.dispatchEvent( new Event( 'change', { bubbles: true } ) );
		}
	}

	#t( message ) {
		return typeof window.wp !== 'undefined' && window.wp.i18n
			? window.wp.i18n.__( message, 'levcharity' )
			: message;
	}
}
